import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tendersAPI } from '../services/api';
import { Search, BookMarked, ChevronLeft, ChevronRight, Building2, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const MOBILE_CSS = `
  @media (max-width: 768px) {
    .mob-grid-2  { grid-template-columns: repeat(2,1fr) !important; }
    .mob-grid-1  { grid-template-columns: 1fr !important; }
    .mob-stack   { grid-template-columns: 1fr !important; }
    .mob-tabs    { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100% !important; }
    .mob-tabs::-webkit-scrollbar { display: none; }
    .mob-filters { grid-template-columns: 1fr !important; gap: 8px !important; }
  }
`;

const URGENCY = {
  critical: { color: 'var(--accent-red)',    dim: 'var(--accent-red-dim)' },
  urgent:   { color: 'var(--accent-orange)', dim: 'var(--accent-orange-dim)' },
  soon:     { color: 'var(--accent-blue)',   dim: 'var(--accent-blue-dim)' },
  normal:   { color: 'var(--accent-green)',  dim: 'var(--accent-green-dim)' },
};

function TenderCard({ tender, onSave }) {
  const navigate = useNavigate();
  const { color } = URGENCY[tender.urgency] || URGENCY.normal;

  return (
    <div
      className="card"
      style={{ cursor: 'pointer', borderLeft: `3px solid ${color}` }}
      onClick={() => navigate(`/tenders/${tender.id}`)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span className={`badge badge-${tender.urgency}`}>{tender.days_remaining}d left</span>
            {tender.category && <span className="badge badge-open">{tender.category}</span>}
            {tender.is_verified && (
              <span className="badge" style={{ background: 'rgba(79,142,247,0.1)', color: 'var(--accent-blue)' }}>✓ Verified</span>
            )}
          </div>
          <h3 style={{ fontSize: '15px', fontWeight: '600', lineHeight: '1.4', marginBottom: '6px' }}>{tender.title}</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{tender.agency} • {tender.state}</p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '17px', fontWeight: '700', color: 'var(--accent-green)', fontFamily: 'Syne' }}>
            {tender.contract_value_formatted || 'N/A'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Contract Value</div>
        </div>
      </div>

      {tender.description && (
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '16px' }}>
          {tender.description.substring(0, 150)}...
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {tender.reference_number ? `Ref: ${tender.reference_number}` : tender.agency}
        </div>
        <div onClick={e => e.stopPropagation()}>
          <button
            className="btn btn-ghost"
            style={{ padding: '6px 12px', fontSize: '12px' }}
            onClick={() => onSave(tender.id)}
          >
            <BookMarked size={13} color={tender.is_saved ? 'var(--accent-green)' : undefined} />
            {tender.is_saved ? ' Saved' : ' Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Onboarding banner — soft prompt to complete company setup ──────
function OnboardingBanner({ navigate }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', background: 'rgba(79,142,247,0.06)', border: '1px solid rgba(79,142,247,0.2)', borderRadius: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
      <Building2 size={18} color="var(--accent-blue)" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '2px' }}>Complete your company profile</div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Add your CAC registration, industry, and focus areas to unlock AI matching and personalised tender feeds.</div>
      </div>
      <button
        onClick={() => navigate('/companies')}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 18px', background: 'var(--accent-blue)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}
      >
        Complete Profile <ArrowRight size={13} />
      </button>
    </div>
  );
}

export default function Tenders() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [tenders, setTenders]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState({ keyword: '', category: '', state: '', sort_by: 'deadline' });

  // Show onboarding banner if user has no company profile data beyond the shell
  // (company_id exists because we create a shell on register, but no industry = incomplete)
  const profileIncomplete = user && !user.company?.industry && !localStorage.getItem('onboarding_done');

  const fetchTenders = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 10, status: 'open', ...filters };
      // Remove empty strings so backend doesn't filter on them
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await tendersAPI.list(params);
      setTenders(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch {
      toast.error('Failed to load tenders');
    } finally {
      setLoading(false);
    }
  };

  // fetchTenders is a plain function, not memoized — its body only reads
  // page/filters (both already below), confirmed by reading it directly.
  // Adding it here would re-run this effect on every render instead of
  // only when page or filters actually change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTenders(); }, [page, filters]);

  // Reset to page 1 when filters change
  const setFilter = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };

  const handleSave = async (id) => {
    try {
      const res = await tendersAPI.save(id);
      toast.success(res.data.message);
      setTenders(prev => prev.map(t => t.id === id ? { ...t, is_saved: res.data.saved } : t));
    } catch {
      toast.error('Failed to save tender');
    }
  };

  return (
    <div>
      <style>{MOBILE_CSS}</style>

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', marginBottom: '6px' }}>Tender Intelligence</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          {loading ? 'Loading...' : `${total.toLocaleString()} active tenders — sorted by deadline`}
        </p>
      </div>

      {/* Onboarding banner — only if company profile incomplete */}
      {profileIncomplete && <OnboardingBanner navigate={navigate} />}

      {/* Filters */}
      <div className="mob-filters" style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            placeholder="Search tenders, agencies, categories..."
            value={filters.keyword}
            onChange={e => setFilter('keyword', e.target.value)}
            style={{ paddingLeft: '38px' }}
          />
        </div>
        <select value={filters.category} onChange={e => setFilter('category', e.target.value)} style={{ width: '160px' }}>
          <option value="">All Categories</option>
          <option value="Oil & Gas">Oil & Gas</option>
          <option value="Construction">Construction</option>
          <option value="Healthcare">Healthcare</option>
          <option value="ICT">ICT</option>
          <option value="Agriculture">Agriculture</option>
          <option value="Energy">Energy</option>
          <option value="Works">Works</option>
          <option value="Goods">Goods</option>
          <option value="Services">Services</option>
          <option value="Consultancy">Consultancy</option>
        </select>
        <select value={filters.state} onChange={e => setFilter('state', e.target.value)} style={{ width: '140px' }}>
          <option value="">All States</option>
          <option value="Federal">Federal</option>
          <option value="Rivers">Rivers</option>
          <option value="Lagos">Lagos</option>
          <option value="Abuja">Abuja</option>
          <option value="Delta">Delta</option>
          <option value="Edo">Edo</option>
          <option value="Bayelsa">Bayelsa</option>
          <option value="Akwa Ibom">Akwa Ibom</option>
          <option value="Kano">Kano</option>
          <option value="Ogun">Ogun</option>
        </select>
        <select value={filters.sort_by} onChange={e => setFilter('sort_by', e.target.value)} style={{ width: '140px' }}>
          <option value="deadline">Sort: Deadline</option>
          <option value="value_high">Sort: Value ↑</option>
          <option value="value_low">Sort: Value ↓</option>
          <option value="newest">Sort: Newest</option>
        </select>
      </div>

      {/* Tender list */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px', color: 'var(--text-muted)', gap: '12px' }}>
          <div style={{ width: '24px', height: '24px', border: '2px solid rgba(0,212,161,0.2)', borderTopColor: 'var(--accent-green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{'@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}'}</style>
          Loading tenders...
        </div>
      ) : tenders.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px', borderStyle: 'dashed' }}>
          <div style={{ width: '64px', height: '64px', background: 'rgba(0,212,161,0.08)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Search size={28} color="var(--accent-green)" style={{ opacity: 0.5 }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
            {filters.keyword || filters.category || filters.state ? 'No tenders match your filters' : 'No tenders loaded yet'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '380px', margin: '0 auto 24px', lineHeight: '1.6' }}>
            {filters.keyword || filters.category || filters.state
              ? 'Try broadening your search or clearing the filters.'
              : 'Tenders are seeded from 38 Nigerian procurement sources every 6 hours. Check back shortly.'}
          </p>
          {(filters.keyword || filters.category || filters.state) && (
            <button className="btn btn-ghost" onClick={() => { setPage(1); setFilters({ keyword: '', category: '', state: '', sort_by: 'deadline' }); }}>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {tenders.map(tender => (
            <TenderCard key={tender.id} tender={tender} onSave={handleSave} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '32px' }}>
          <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
